import React, { useEffect, useRef, useState } from 'react';

interface TextProps {
  x: number;
  y: number;
  width: number;
  height: number;
  children: string;
  style?: React.CSSProperties;
  lineHeight?: number;
  capHeight?: number;
  textAnchor?: 'start' | 'middle' | 'end';
  fontFamily?: string;
  color?: string;
  backgroundColor?: string;
}

export const MultilineSvgText: React.FC<TextProps> = ({
  x,
  y,
  width,
  height,
  children,
  style = {},
  lineHeight = 1,
  capHeight = 0.71,
  textAnchor = 'start',
  fontFamily,
  backgroundColor = 'rgba(0, 0, 0, 0.1)',
  color
}) => {
  const [lines, setLines] = useState<string[]>([]);
  const wordsWithWidth = useRef<{ word: string; width: number }[]>([]);
  const spaceWidth = useRef<number>(0);

  const calculateWordWidths = () => {
    const words = children.split(/\s+/);
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    Object.assign(text.style, style);
    if (fontFamily) text.setAttribute('font-family', fontFamily);
    svg.appendChild(text);
    document.body.appendChild(svg);

    wordsWithWidth.current = words.map((word) => {
      text.textContent = word;
      return { word, width: text.getComputedTextLength() };
    });

    text.textContent = '\u00A0'; // space
    spaceWidth.current = text.getComputedTextLength();

    document.body.removeChild(svg);
  };

  const calculateLines = () => {
    const lines: string[][] = [];
    let currentLine: string[] = [];
    let currentLineWidth = 0;

    const measureText = (text: string): number => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const textEl = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'text'
      );
      Object.assign(textEl.style, style);
      if (fontFamily) textEl.setAttribute('font-family', fontFamily);
      textEl.textContent = text;
      svg.appendChild(textEl);
      document.body.appendChild(svg);
      const width = textEl.getComputedTextLength();
      document.body.removeChild(svg);
      return width;
    };

    for (const { word, width: wordWidth } of wordsWithWidth.current) {
      const space = currentLine.length > 0 ? spaceWidth.current : 0;

      if (currentLineWidth + space + wordWidth <= width) {
        currentLine.push(word);
        currentLineWidth += space + wordWidth;
      } else if (wordWidth <= width) {
        if (currentLine.length) lines.push(currentLine);
        currentLine = [word];
        currentLineWidth = wordWidth;
      } else {
        // Word is too long — break it letter by letter
        if (currentLine.length) {
          lines.push(currentLine);
          currentLine = [];
          currentLineWidth = 0;
        }

        let partial = '';
        let partialWidth = 0;
        for (const char of word) {
          const charWidth = measureText(char);
          if (partialWidth + charWidth > width) {
            lines.push([partial]);
            partial = char;
            partialWidth = charWidth;
          } else {
            partial += char;
            partialWidth += charWidth;
          }
        }
        if (partial) {
          currentLine = [partial];
          currentLineWidth = partialWidth;
        }
      }
    }

    if (currentLine.length) lines.push(currentLine);
    return lines.map((words) => words.join(' '));
  };

  const getAnchorOffset = (line: string) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    Object.assign(text.style, style);
    if (fontFamily) text.setAttribute('font-family', fontFamily);
    svg.appendChild(text);
    document.body.appendChild(svg);

    text.textContent = line;
    const lineWidth = text.getComputedTextLength();

    document.body.removeChild(svg);

    switch (textAnchor) {
      case 'middle':
        return lineWidth / 2 - width / 2;
      case 'end':
        return lineWidth - width;
      default:
        return 0;
    }
  };

  useEffect(() => {
    calculateWordWidths();
    setLines(calculateLines());
  }, [children, style, width]);

  // Max number of lines that fit in the height
  const maxLines = Math.floor(height / lineHeight);

  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Background rectangle */}
      <text
        style={style}
        color={color}
        //clipPath="url(#text-clip)"
      >
        {lines.slice(0, maxLines).map((line, index) => (
          <tspan
            key={index}
            x={-getAnchorOffset(line)}
            dy={`${lineHeight}em`}
            style={{ fontFamily, color }}
            color={color}
          >
            {line}
          </tspan>
        ))}
      </text>
      {/* Clip path to hide overflowed lines (optional fallback for strict SVG) */}
      <clipPath id="text-clip">
        <rect x={0} y={0} width={width} height={height} />
      </clipPath>
    </g>
  );
};
